import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../context/TitleContext";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchContacts,
  deleteContact,
  replyContact,
  clearContactState,
  fetchContactHistory,
  clearContactHistory,
} from "../../redux/Slices/ContactSlice";
import { toast } from "react-toastify";
import { usePermissions } from "../../hooks/usePermissions";
import Pagination from '../../components/Pagination';
import { FaReply, FaTrash } from "react-icons/fa";
import { maskPhone } from "../../Utils/privacy";

/* ─── tiny helpers ─────────────────────────────────────────── */
const getReplierName = (c) =>
  c?.replied_by?.name ||
  c?.replied_by_name ||
  c?.admin?.name ||
  c?.admin_name ||
  c?.responder?.name ||
  (typeof c?.replied_by === "string" ? c.replied_by : null);

const formatDate = (ds) => {
  if (!ds) return "—";
  return new Date(ds).toLocaleString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ─── StatusBadge ───────────────────────────────────────────── */
const StatusBadge = ({ status }) =>
  status === "replied" ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 20,
        background: "#d1fae5",
        color: "#065f46",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 8 }}>●</span> تم الرد
    </span>
  ) : (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px",
        borderRadius: 20,
        background: "#fef3c7",
        color: "#92400e",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 8 }}>●</span> قيد الانتظار
    </span>
  );

/* ─── AvatarLetter ─────────────────────────────────────────── */
const AvatarLetter = ({ name }) => {
  const colors = ["var(--main-color)","#ec4899","#0ea5e9","#f59e0b","var(--green-color)","var(--red-color)","#8b5cf6","#14b8a6"];
  const idx = (name?.charCodeAt(0) || 0) % colors.length;
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: colors[idx],
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 15,
        flexShrink: 0,
      }}
    >
      {name?.charAt(0) || "؟"}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════ */
const Contacts = () => {
  const { t } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const {
    data: contacts,
    meta,
    loading,
    error,
    deleteLoading,
    replyLoading,
    success,
    history,
    historyLoading,
    historyError,
    historyTruncated,
  } = useSelector((state) => state.contacts);

  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deletingContact, setDeletingContact]   = useState(null);
  const [showReplyModal, setShowReplyModal]     = useState(false);
  const [replyingContact, setReplyingContact]   = useState(null);
  const [adminReply, setAdminReply]             = useState("");
  const [currentPage, setCurrentPage]           = useState(1);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyContact, setHistoryContact]     = useState(null);
  const [statusFilter, setStatusFilter]         = useState("");
  const { hasPermission } = usePermissions();

  useEffect(() => { setTitle(t("sidenav.contacts")); dispatch(clearContactState()); }, [setTitle, t, dispatch]);
  useEffect(() => { dispatch(fetchContacts({ page: currentPage, status: statusFilter })); }, [dispatch, currentPage, statusFilter]);

  // تحديث تلقائي: جلب الرسائل الجديدة كل 30 ثانية (يتخطى وقت فتح أي مودال حتى لا يقاطع المستخدم)
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!showReplyModal && !showDeleteModal && !showHistoryModal) {
        dispatch(fetchContacts({ page: currentPage, status: statusFilter }));
      }
    }, 30000);
    return () => clearInterval(intervalId);
  }, [dispatch, currentPage, statusFilter, showReplyModal, showDeleteModal, showHistoryModal]);

  useEffect(() => { if (error) toast.error(error); }, [error]);

  useEffect(() => {
    if (success) {
      if (deletingContact) {
        setShowDeleteModal(false); setDeletingContact(null);
        setTimeout(() => toast.success("تم حذف الرسالة بنجاح", { position: "top-center" }), 100);
      } else if (replyingContact) {
        setShowReplyModal(false); setReplyingContact(null); setAdminReply("");
        setTimeout(() => toast.success("تم الرد على الرسالة بنجاح", { position: "top-center" }), 100);
        if (showHistoryModal && historyContact) dispatch(fetchContactHistory(historyContact.phone));
      }
      dispatch(clearContactState());
    }
  }, [success, deletingContact, replyingContact, dispatch, showHistoryModal, historyContact]);

  /* ── handlers ─────────────────────────────────────── */
  const handleStatusFilter = (v) => { setStatusFilter(v); setCurrentPage(1); };
  const handleDelete       = (c) => { setDeletingContact(c); setShowDeleteModal(true); };
  const confirmDelete      = () => { if (deletingContact) { dispatch(deleteContact(deletingContact.id)); setShowDeleteModal(false); } };
  const handleReplyClick   = (c) => { setReplyingContact(c); setAdminReply(c.admin_reply || ""); setShowReplyModal(true); };
  const handleViewHistory  = (c) => { setHistoryContact(c); setShowHistoryModal(true); dispatch(fetchContactHistory(c.phone)); };
  const closeHistory       = () => { setShowHistoryModal(false); setHistoryContact(null); dispatch(clearContactHistory()); };
  const submitReply        = () => { if (replyingContact && adminReply.trim()) dispatch(replyContact({ id: replyingContact.id, admin_reply: adminReply })); else toast.error("الرجاء إدخال نص الرد"); };
  const handlePageChange   = (p) => { if (!loading && p !== currentPage) setCurrentPage(p); };

  /* ── stats ─────────────────────────────────────────── */
  const totalReplied  = contacts.filter((c) => c.status === "replied").length;
  const totalPending  = contacts.filter((c) => c.status !== "replied").length;

  /* ── Pagination ────────────────────────────────────── */


  /* ── shared overlay style ──────────────────────────── */
  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const modalBox = (w = 480) => ({ background: "#fff", borderRadius: 16, width: "100%", maxWidth: w, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden", display: "flex", flexDirection: "column" });

  /* ════════════════════════════════════════════════════ */
  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, color: "#0f172a", fontSize: 20 }}>
            📨 {t("sidenav.contacts")}
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>
            إجمالي الرسائل في هذه الصفحة — مُرَد عليها: <strong style={{ color: "var(--green-color)" }}>{totalReplied}</strong> &nbsp;|&nbsp; قيد الانتظار: <strong style={{ color: "#f59e0b" }}>{totalPending}</strong>
          </p>
        </div>

        {/* filter tabs */}
        <div style={{ display: "flex", gap: 6, background: "#f1f5f9", borderRadius: 10, padding: 4 }}>
          {[
            { val: "",         label: "الكل",        color: "var(--main-color)" },
            { val: "replied",  label: "تم الرد",     color: "var(--green-color)" },
            { val: "pending",  label: "قيد الانتظار", color: "#f59e0b" },
          ].map((f) => (
            <button
              key={f.val}
              onClick={() => handleStatusFilter(f.val)}
              disabled={loading}
              style={{
                padding: "6px 16px", borderRadius: 7, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .15s",
                background: statusFilter === f.val ? f.color : "transparent",
                color: statusFilter === f.val ? "#fff" : "#64748b",
              }}
            >{f.label}</button>
          ))}
        </div>
      </div>

      {/* ── Loading skeleton ────────────────────────────── */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: 72, borderRadius: 12, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
          <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────── */}
      {!loading && contacts.length === 0 && (
        <div style={{ textAlign: "center", padding: "64px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 56 }}>📭</div>
          <p style={{ marginTop: 12, fontSize: 16, fontWeight: 600, color: "#64748b" }}>{t("labels.noData")}</p>
        </div>
      )}

      {/* ── Contact Cards ───────────────────────────────── */}
      {!loading && contacts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {contacts.map((contact, index) => (
            <div
              key={contact.id}
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                transition: "box-shadow .2s",
                boxShadow: "0 1px 4px rgba(0,0,0,.04)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(99,102,241,.12)")}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)")}
            >
              {/* avatar */}
              <AvatarLetter name={contact.name} />

              {/* main info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => handleViewHistory(contact)}
                    style={{ background: "none", border: "none", padding: 0, fontWeight: 700, color: "#0f172a", fontSize: 15, cursor: "pointer", textDecoration: "none" }}
                  >
                    {contact.name}
                  </button>
                  <span style={{ color: "#94a3b8", fontSize: 12 }} title="رقم محجوب لحماية الخصوصية">{maskPhone(contact.phone)}</span>
                  <StatusBadge status={contact.status} />
                  {contact.status === "replied" && getReplierName(contact) && (
                    <span style={{ fontSize: 11, color: "#64748b" }}>🎧 {getReplierName(contact)}</span>
                  )}
                </div>

                {contact.subject && (
                  <div style={{ color: "var(--main-color)", fontSize: 12, fontWeight: 600, marginTop: 2 }}>
                    {contact.subject}
                  </div>
                )}

                <p
                  style={{ margin: "3px 0 0", color: "#64748b", fontSize: 13, maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                  title={contact.message}
                >
                  {contact.message}
                </p>
              </div>

              {/* date */}
              <div style={{ flexShrink: 0, textAlign: "center", color: "#94a3b8", fontSize: 11, minWidth: 80 }}>
                {formatDate(contact.created_at)}
              </div>

              {/* row number */}
              <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                {(meta?.current_page - 1) * (meta?.per_page || 10) + index + 1}
              </div>

              {/* actions */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => handleViewHistory(contact)}
                  title="سجل المحادثة"
                  style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #e0e7ff", background: "#eef2ff", color: "var(--main-color)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  💬
                </button>
                {hasPermission("reply-contacts") && (
                  <button
                    onClick={() => handleReplyClick(contact)}
                    title="رد"
                    disabled={deleteLoading || replyLoading}
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <FaReply size={14} />
                  </button>
                )}
                {hasPermission("delete-contacts") && (
                  <button
                    onClick={() => handleDelete(contact)}
                    title="حذف"
                    disabled={deleteLoading || replyLoading}
                    style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <FaTrash size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />}

      {/* ════════════════════════════════════════════════════
          Delete Modal
      ════════════════════════════════════════════════════ */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={modalBox()}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 17, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeletingContact(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف رسالة <strong>"{deletingContact?.name}"</strong>؟</p>
              <p style={{ margin: "8px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeletingContact(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={deleteLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleteLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف الرسالة"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          Reply Modal
      ════════════════════════════════════════════════════ */}
      {showReplyModal && (
        <div style={{ ...overlay, zIndex: 1060 }}>
          <div style={modalBox(520)}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>↩ الرد على رسالة "{replyingContact?.name}"</span>
              <button onClick={() => { setShowReplyModal(false); setReplyingContact(null); setAdminReply(""); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>الرسالة الأصلية</div>
                <div style={{ padding: "12px 14px", background: "#f8fafc", borderRadius: 10, color: "#334155", fontSize: 14, lineHeight: 1.6, border: "1px solid #e2e8f0" }}>
                  {replyingContact?.message}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6, display: "block" }}>
                  نص الرد <span style={{ color: "var(--red-color)" }}>*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="اكتب ردك هنا..."
                  value={adminReply}
                  onChange={(e) => setAdminReply(e.target.value)}
                  disabled={replyLoading}
                  style={{ width: "100%", borderRadius: 10, border: "1.5px solid #e2e8f0", padding: "10px 14px", fontSize: 14, resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--main-color)")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowReplyModal(false); setReplyingContact(null); setAdminReply(""); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={submitReply} disabled={replyLoading || !adminReply.trim()} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--main-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, opacity: !adminReply.trim() ? 0.5 : 1 }}>
                {replyLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الإرسال...</> : "إرسال الرد ↩"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          History Modal  (chat-style)
      ════════════════════════════════════════════════════ */}
      {showHistoryModal && (
        <div style={{ ...overlay, zIndex: 1050 }}>
          <div style={{ ...modalBox(640), maxHeight: "85vh" }}>
            {/* header */}
            <div style={{ padding: "16px 22px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 12 }}>
              <AvatarLetter name={historyContact?.name} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>{historyContact?.name}</div>
                <div style={{ color: "#64748b", fontSize: 12 }} title="رقم محجوب لحماية الخصوصية">{maskPhone(historyContact?.phone)}</div>
              </div>
              <button onClick={closeHistory} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>

            {/* body */}
            <div style={{ flex: 1, overflowY: "auto", padding: 18, background: "#f0f4f8", minHeight: 200 }}>
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <div className="spinner-border" role="status"></div>
                  <p style={{ marginTop: 12 }}>جاري تحميل سجل المحادثة...</p>
                </div>
              ) : historyError ? (
                <div style={{ background: "#fef2f2", color: "var(--red-color)", padding: 16, borderRadius: 10, textAlign: "center" }}>{historyError}</div>
              ) : history.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                  <div style={{ fontSize: 48 }}>📭</div>
                  <p style={{ marginTop: 10 }}>لا توجد رسائل سابقة لهذا العميل</p>
                </div>
              ) : (
                <>
                  {historyTruncated && (
                    <div style={{ background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "8px 14px", fontSize: 12, marginBottom: 14, textAlign: "center" }}>
                      يتم عرض أحدث المحادثات فقط (السجل كبير جداً)
                    </div>
                  )}
                  {history.map((msg) => (
                    <div key={msg.id} style={{ marginBottom: 18 }}>
                      {/* client bubble */}
                      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 6 }}>
                        <div style={{ background: "#fff", borderRadius: "0 12px 12px 12px", padding: "10px 14px", maxWidth: "72%", boxShadow: "0 1px 4px rgba(0,0,0,.08)" }}>
                          {msg.subject && (
                            <div style={{ color: "var(--main-color)", fontWeight: 700, fontSize: 12, marginBottom: 4 }}>{msg.subject}</div>
                          )}
                          <div style={{ color: "#334155", fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>{msg.message}</div>
                          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 6, textAlign: "left" }}>{formatDate(msg.created_at)}</div>
                        </div>
                      </div>

                      {/* admin reply bubble */}
                      {msg.admin_reply ? (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <div style={{ background: "linear-gradient(135deg,var(--main-color),var(--main-light))", borderRadius: "12px 0 12px 12px", padding: "10px 14px", maxWidth: "72%", boxShadow: "0 2px 8px rgba(99,102,241,.25)" }}>
                            <div style={{ color: "rgba(255,255,255,.75)", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                              🎧 {getReplierName(msg) ? `رد الدعم — ${getReplierName(msg)}` : "رد الدعم"}
                            </div>
                            <div style={{ color: "#fff", fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>{msg.admin_reply}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600 }}>⏳ بانتظار الرد</span>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* footer */}
            <div style={{ padding: "14px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff" }}>
              {hasPermission("reply-contacts") && historyContact && (
                <button
                  onClick={() => {
                    const target = history.length > 0 ? history[history.length - 1] : historyContact;
                    handleReplyClick(target);
                  }}
                  disabled={historyLoading}
                  style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--main-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                >
                  ↩ رد
                </button>
              )}
              <button onClick={closeHistory} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", marginRight: "auto" }}>
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;